import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Pagination } from 'antd';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import sortBy from 'lodash-es/sortBy';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { MSTeamsEntity, MSTeamsUser, TargetRespondent } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSearch } from 'jf/components/DevExSearch';
import { DevExText } from 'jf/components/DevExText';

import { UnlinkedUserWarning } from '../UnlinkedUserWarning';

import { EmptyMSTeamsRosterDisplay } from './EmptyMSTeamsRosterDisplay';
import { MSTeamsEntityDisplay } from './MSTeamsEntityDisplay';

type MSTeamsTargetListProps = {
  users: MSTeamsUser[];
  unlinkedUsers?: TargetRespondent[]; // list of unlinked users
  onLink?: () => void; // on link of unlinked msteams user
  onUpdate: (users: MSTeamsUser[]) => void;
  loading?: boolean;
  emptyText?: string | React.ReactElement; // text to display when list is empty
  emptyStateCTA?: string | React.ReactElement; // empty state secondary text button or text
  extra?: React.ReactElement[]; // additional header buttons
  usersLabel?: string | React.ReactElement; // label for user name, repondents or msteams user etc
  trackEventKey: string; // key for tracking events
  loadingText?: string; // loading message
  saving?: boolean; // Saving or removing msteams targets
};

const USERS_PER_PAGE = 10;

const styles = css`
  width: 100%;
  .msteams-target-list__loading,
  .msteams-target-list__empty {
    padding: ${({ theme }) => theme.variable.spacing.md};
    border: 1px solid ${({ theme }) => theme.color.border.secondary};
    border-radius: 5px;
    .empty__text-content,
    .loading__text-content {
      width: 80%;
      text-align: center;
    }
  }
`;
export const MSTeamsTargetList: React.FC<MSTeamsTargetListProps> = ({
  users,
  onUpdate,
  unlinkedUsers,
  onLink,
  loading,
  emptyText,
  emptyStateCTA,
  extra,
  usersLabel,
  trackEventKey,
  loadingText,
  saving,
}) => {
  const theme = useDevExTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const sortedMSTeamsUsers = useMemo(
    () => sortBy(users, (target) => target && target.displayName.toLowerCase()),
    [users]
  );
  const sortedUnlinkedUsers = useMemo(
    () =>
      unlinkedUsers
        ? sortBy(unlinkedUsers, (target) =>
            target && target.name ? target.name.toLowerCase() : target.email.toLowerCase()
          )
        : [],
    [unlinkedUsers]
  );
  const filteredUsers: {
    msteamsUsers: MSTeamsUser[];
    unlinked: TargetRespondent[];
  } = useMemo(() => {
    const msteamsUsers = sortedMSTeamsUsers.filter(
      (studyMSTeamsTarget) =>
        studyMSTeamsTarget &&
        ((studyMSTeamsTarget.displayName &&
          studyMSTeamsTarget.displayName.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (studyMSTeamsTarget.email &&
            studyMSTeamsTarget.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (studyMSTeamsTarget.name &&
            studyMSTeamsTarget.name.toLowerCase().includes(searchQuery.toLowerCase())))
    );
    const unlinked = sortedUnlinkedUsers.filter(
      (unlinkedTarget) =>
        unlinkedTarget &&
        ((unlinkedTarget.name &&
          unlinkedTarget.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          unlinkedTarget.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    return {
      msteamsUsers,
      unlinked,
    };
  }, [sortedMSTeamsUsers, searchQuery]);

  const filteredUsersByPage: MSTeamsUser[] = useMemo(() => {
    const startIndex = (page - 1) * USERS_PER_PAGE;
    const endIndex = page * USERS_PER_PAGE;
    return filteredUsers.msteamsUsers.slice(startIndex, endIndex);
  }, [page, filteredUsers]);

  useEffect(() => {
    if ((page + 1) * USERS_PER_PAGE < filteredUsers.msteamsUsers.length) {
      setPage(1);
    }
  }, [filteredUsers]);

  const waitingForSync = false;

  const [editUsersActive, setEditUsersActive] = useState(false);

  const showUnlinked = editUsersActive && unlinkedUsers && !!unlinkedUsers.length && onLink;
  return (
    <Flex
      // prevent further interactions while the msteams list is saving.
      // this will prevent conflicting adds or removes that are not honored.
      style={saving ? { pointerEvents: 'none', opacity: 0.4 } : {}}
      css={styles}
      vertical
      gap={theme.variable.spacing.sm}
    >
      {!!sortedUnlinkedUsers.length && !!users.length && (
        <UnlinkedUserWarning
          unlinkedUsers={sortedUnlinkedUsers}
          enabled={editUsersActive}
          onClick={() => setEditUsersActive(!editUsersActive)}
          messagingType="MS Teams"
        />
      )}
      {loading ? (
        <Flex
          className="msteams-target-list__loading"
          vertical
          align="center"
          justify="center"
          gap={theme.variable.spacing.lg}
        >
          <DevExLoader />
          <DevExText fontSize="lg">{loadingText ? loadingText : 'Searching MS Teams...'}</DevExText>
          {waitingForSync && ( // backend says that users aren't ready
            <DevExText fontSize="md">
              This is taking a bit longer than usual; please allow a couple more minutes. We
              appreciate your patience!
            </DevExText>
          )}
        </Flex>
      ) : (
        <>
          <Flex align="center" justify="space-between">
            <>
              {usersLabel || (
                <div style={{ color: theme.color.text.secondary }}>
                  {users.length || 0} MS Teams user
                  {users.length !== 1 ? 's' : ''} found
                </div>
              )}
            </>
            {saving && (
              <Flex align="center">
                <DevExLoader size="small" />
                <DevExText style={{ verticalAlign: 'middle' }} type="secondary" fontSize="sm">
                  Saving
                </DevExText>
              </Flex>
            )}
            <Flex gap={theme.variable.spacing.sm} align="center" justify="space-between">
              {!!users.length && (
                <DevExButton
                  size="default"
                  type="text"
                  onClick={() => {
                    trackEvent(`survey-editor:${trackEventKey}:remove-all`);
                    onUpdate([]);
                  }}
                  style={{ marginLeft: `-${theme.variable.spacing.sm}` }}
                >
                  Remove all
                </DevExButton>
              )}
              <DevExSearch
                size="small"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                collapsible
                disabled={users.length === 0}
                outline
              />
              {extra && extra.map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>)}
            </Flex>
          </Flex>
          {showUnlinked || users.length ? (
            <>
              {/* Users that we found without msteams, and we've clicked the "link users" */}
              {showUnlinked &&
                filteredUsers.unlinked.map((user, i) => (
                  <Flex
                    key={user.publicReferenceId}
                    gap={theme.variable.spacing.xxl}
                    align="center"
                    justify="space-between"
                    style={{
                      padding: theme.variable.spacing.sm,
                      border: `1px solid`,
                      borderRadius: theme.variable.borderRadius,
                      borderColor: theme.color.border.primary,
                    }}
                  >
                    <EmptyMSTeamsRosterDisplay
                      user={user}
                      rosterUsers={filteredUsers.unlinked}
                      onAdd={() => {
                        setSearchQuery('');
                        onLink();
                      }}
                    />
                  </Flex>
                ))}
              {filteredUsersByPage.map((target, i) => (
                <MSTeamsEntityDisplay
                  key={target.id}
                  msteamsUser={target}
                  entityType={MSTeamsEntity.type.USER}
                  onDelete={() => {
                    const _users = [...users];
                    _users.splice(
                      users.findIndex((u) => u.email === target.email),
                      1
                    );

                    trackEvent(`survey-editor:${trackEventKey}:remove`, {
                      msteamsUserId: target.id || null,
                    });
                    onUpdate(_users);
                  }}
                />
              ))}
              <Pagination
                current={page}
                hideOnSinglePage
                showSizeChanger={false}
                total={filteredUsers.msteamsUsers.length}
                pageSize={USERS_PER_PAGE}
                onChange={(page) => {
                  trackEvent(`study-editor:${trackEventKey}:pagination`, {
                    page,
                  });
                  setPage(page);
                }}
                style={{ alignSelf: 'center' }}
              />
            </>
          ) : (
            <Flex
              className="msteams-target-list__loading"
              align="center"
              gap={theme.variable.spacing.lg}
              vertical
            >
              <DevExEmpty icon={faUser} iconSize={34} />
              <Flex
                className="empty__text-content"
                vertical
                align="center"
                gap={theme.variable.spacing.lg}
              >
                <DevExText fontSize="md" type="secondary" lined>
                  {emptyText ? emptyText : 'No Users'}
                </DevExText>
                {!!emptyStateCTA && (
                  <DevExText fontSize="md" type="secondary" lined>
                    {emptyStateCTA}
                  </DevExText>
                )}
              </Flex>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
